
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { auth } from '@/firebase';
import BaseBreadcrumbs from '@/components/base/BaseBreadcrumbs.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { ROUTER } from '@/router/constants';
import { TeamUserMixin } from '@/mixins/TeamUserMixin';
import {
	listConnectedDevices,
	revokeDevice,
	signOutEverywhere,
} from '@/utils/devices';
import type { ConnectedDevice } from '@/utils/devices';
import {
	clearWebClientDeviceId,
	ensureWebClientDeviceId,
	readWebClientDeviceId,
} from '@/utils/webClientDevice';

const API_BASE_URL =
	process.env.VUE_APP_API_BASE_URL || 'https://api2.rundiffusion.com/api/v2';

type State = 'loading' | 'ready' | 'signed_out' | 'error';

export default Vue.extend({
	name: 'Devices',
	mixins: [
		TeamUserMixin,
	],
	components: {
		BaseBreadcrumbs,
		ConfirmDialog,
	},
	metaInfo: {
		title: 'Connected devices | RunDiffusion',
	},
	data () {
		return {
			state: 'loading' as State,
			errorMessage: '',
			devices: [] as ConnectedDevice[],
			// This browser's own device id, so its row can be marked and offer
			// marked as the one you are on. Null until it registers.
			currentDeviceId: readWebClientDeviceId() as string | null,
			busyId: null as string | null,
			busyEverywhere: false,
			// Track which device rows had a logo load failure so we can
			// gracefully fall back to mdi-puzzle-outline. Keyed by device id.
			logoFailed: {} as Record<string, boolean>,
			// Confirm-dialog open flags. The disconnect dialog also remembers
			// which device the user clicked so onConfirm knows what to revoke.
			disconnectDialogOpen: false,
			pendingDisconnectId: null as string | null,
			signOutEverywhereDialogOpen: false,
		};
	},
	computed: {
		...mapState(['authUser']),
		breadcrumbs () {
			// Manage Devices lives under the Plugins surface: Plugins -> Manage Devices
			// (no Company Dashboard crumb, since device management is a plugin concern).
			const crumbs = [
				{
					hoverColor: 'var(--primary-blue)',
					text: 'Manage Devices',
					disabled: true,
					to: { name: ROUTER.AUTH_DEVICES },
				},
			];
			crumbs.unshift({
				hoverColor: 'var(--primary-blue)',
				text: this.pluginsSurfaceLabel,
				disabled: false,
				to: { name: ROUTER.PLUGINS },
			});
			return crumbs;
		},
		// Revoked devices are kept in Firestore as an audit trail but
		// hidden from the user. The user-facing model is: a device is
		// either connected, or it isn't.
		visibleDevices (): ConnectedDevice[] {
			return this.devices.filter((d: ConnectedDevice) => !d.revoked_at);
		},
		// Drives the confirm dialog's wording: signing yourself out of this
		// browser is a different act from cutting off a device you are not at.
		disconnectingCurrentDevice (): boolean {
			return !!this.pendingDisconnectId && this.isCurrentDevice(this.pendingDisconnectId);
		},
	},
	watch: {
		authUser () {
			void this.init();
		},
	},
	mounted () {
		void this.init();
	},
	methods: {
		...mapActions(['updateSnackbar']),
		// Back link in the page header. Always returns to the Plugins page.
		// This is the canonical "parent" surface for managing connected plugin devices.
		goBackToPluginsApi () {
			this.$router.push({ name: ROUTER.PLUGINS });
		},
		// Pull the current Firebase user's id token, or flip the page into
		// signed_out state if Firebase no longer has a user (which can
		// happen mid-action when another tab signs out, or when the page
		// itself ran sign-out-everywhere). Returns null on bail-out so
		// callers can short-circuit cleanly.
		async getIdTokenOrBail (): Promise<string | null> {
			const user = auth.currentUser;
			if (!user) {
				this.state = 'signed_out';
				this.devices = [];
				return null;
			}
			return user.getIdToken();
		},
		// Map a kernel result.reason to user-readable copy. Raw reason
		// codes like "network" / "forbidden" are fine for logs but not
		// for snackbars.
		readableReason (reason: string): string {
			if (reason === 'network') return 'Network error. Check your connection and try again.';
			if (reason === 'forbidden') return 'You don’t have permission to perform that action.';
			if (reason === 'not_found') return 'That plugin entry no longer exists.';
			return 'Something went wrong. Please try again.';
		},
		/**
		 * Register this browser as a Connected Device, once, so it appears in
		 * its own list.
		 *
		 * Deliberately NOT part of `load()`. `load()` also runs after a revoke,
		 * and registering there made disconnecting this browser un-performable:
		 * revoking it cleared the stored id, the reload immediately minted a new
		 * one, and every attempt left another revoked record behind.
		 * Disconnecting has to be able to stay disconnected.
		 */
		async ensureRegistered () {
			if (!this.authUser) return;
			const idToken = await this.getIdTokenOrBail();
			if (!idToken) return;
			this.currentDeviceId = await ensureWebClientDeviceId({
				baseUrl: API_BASE_URL,
				idToken,
			});
		},
		/** Arriving at the page: register this browser, then show the list. */
		async init () {
			await this.ensureRegistered();
			await this.load();
		},
		async load () {
			if (!this.authUser) {
				this.state = 'signed_out';
				return;
			}
			this.state = 'loading';
			try {
				const idToken = await this.getIdTokenOrBail();
				if (!idToken) return;

				const result = await listConnectedDevices({
					baseUrl: API_BASE_URL,
					idToken,
				});
				if (result.ok) {
					this.devices = result.devices;
					this.state = 'ready';
				} else {
					this.state = 'error';
					const reason = (result as { reason: string }).reason;
					this.errorMessage =
						reason === 'network' ? 'Network error.' : 'Server error.';
				}
			} catch (err) {
				this.state = 'error';
				this.errorMessage = err instanceof Error ? err.message : String(err);
			}
		},
		// Click Disconnect on a row → open the confirm dialog. The actual
		// revoke fires from onConfirmDisconnect once the user confirms.
		disconnect (deviceId: string) {
			this.pendingDisconnectId = deviceId;
			this.disconnectDialogOpen = true;
		},
		async onConfirmDisconnect (close: () => void) {
			const deviceId = this.pendingDisconnectId;
			if (!deviceId) {
				close();
				return;
			}
			this.busyId = deviceId;
			try {
				const idToken = await this.getIdTokenOrBail();
				if (!idToken) {
					close();
					return;
				}
				const result = await revokeDevice({
					baseUrl: API_BASE_URL,
					idToken,
					deviceId,
				});
				if (result.ok) {
					close();
					const wasCurrentDevice = this.isCurrentDevice(deviceId);
					this.pendingDisconnectId = null;

					if (wasCurrentDevice) {
						// Disconnecting the browser you are ON has to end the session
						// too. Revoking the device record only stops the API accepting
						// that device id; the browser's own sign-in is a separate thing
						// and survives it, so without this you stay signed in holding a
						// session every later request answers with TOKEN_REVOKED.
						//
						// (Sign-out-everywhere gets this for free: it revokes the
						// refresh token, and the auth listener notices. Revoking one
						// device does not touch the token, so we end the session here.)
						clearWebClientDeviceId();
						this.currentDeviceId = null;
						await auth.signOut();

						// Hard navigation rather than a router push, matching App.vue's
						// own sign-out: onIdTokenChanged only nulls the auth user, it
						// does not unbind vuex state or the Firestore listeners hanging
						// off it. Landing on /login is also the confirmation, which is
						// why there is no snackbar here — a full page load would
						// discard it anyway.
						window.location.assign(`${window.location.origin}/login`);
						return;
					}

					// load() refetches; the now-revoked device drops out of
					// visibleDevices automatically since we filter on
					// revoked_at.
					await this.load();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Couldn’t disconnect. ${this.readableReason((result as { reason: string }).reason)}`,
						show: true,
					});
					close();
				}
			} finally {
				this.busyId = null;
			}
		},
		onSignOutEverywhere () {
			this.signOutEverywhereDialogOpen = true;
		},
		async onConfirmSignOutEverywhere (close: () => void) {
			this.busyEverywhere = true;
			try {
				const idToken = await this.getIdTokenOrBail();
				if (!idToken) {
					close();
					return;
				}
				const result = await signOutEverywhere({
					baseUrl: API_BASE_URL,
					idToken,
				});
				if (result.ok) {
					close();
					// Signing out everywhere includes here. This used to rely on
					// Firebase's listener racing to notice the revoked refresh token,
					// which left the outcome to timing: sometimes the page dropped to
					// /login, sometimes it sat there looking signed in with a session
					// the API would no longer accept. Same ending as the single-device
					// path, reached deliberately rather than by race.
					clearWebClientDeviceId();
					this.currentDeviceId = null;
					await auth.signOut();

					// Hard navigation, matching App.vue's own sign-out: the auth
					// listener only nulls the user, it does not unbind vuex or the
					// Firestore listeners hanging off it.
					window.location.assign(`${window.location.origin}/login`);
					return;
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Couldn’t sign out everywhere. ${this.readableReason((result as { reason: string }).reason)}`,
						show: true,
					});
					close();
				}
			} finally {
				this.busyEverywhere = false;
			}
		},
		/**
		 * A RunDiffusion staff member's feedback agent (tools/feedback, restServer apiv2/agent/):
		 * named, with the computer it runs on, so it can be told apart and signed out.
		 */
		feedbackAgentHost (d: ConnectedDevice): string | null {
			return d.client_kind === 'feedback_agent' ? (d.host || 'a computer') : null;
		},
		deviceTitle (d: ConnectedDevice): string {
			const host: string | null = d.plugin_kind ? null : this.feedbackAgentHost(d);
			return host ? `Feedback agent on ${host}` : this.prettyKind(d.plugin_kind);
		},
		deviceIcon (d: ConnectedDevice): string {
			return !d.plugin_kind && this.feedbackAgentHost(d) ? 'mdi-robot-outline' : this.kindIcon(d.plugin_kind);
		},
		prettyKind (kind: string | null): string {
			if (!kind) return 'Unknown connection';
			const map: Record<string, string> = {
				photoshop: 'RunDiffusion for Photoshop',
				miro: 'RunDiffusion for Miro',
				revit: 'RunDiffusion for Revit',
				web_app: 'RunDiffusion in your browser',
				cli: 'RunDiffusion CLI',
				smoke: 'Smoke test',
			};
			return map[kind] || kind;
		},
		/**
		 * True for the row describing the browser this page is running in.
		 *
		 * Compared against the id this browser stored when it registered, so it
		 * survives a reload and never guesses from user-agent. A browser that has
		 * not registered yet simply matches nothing.
		 */
		isCurrentDevice (deviceId: string): boolean {
			return !!this.currentDeviceId && deviceId === this.currentDeviceId;
		},
		/**
		 * Fallback glyph for a kind with no brand logo.
		 *
		 * The browser is not a plugin, so a puzzle piece misdescribes it: that
		 * icon is the visual shorthand for "an app you installed into something
		 * else", which is the one thing this row is not.
		 */
		kindIcon (kind: string | null): string {
			return kind === 'web_app' ? 'mdi-web' : 'mdi-puzzle-outline';
		},
		kindLogoUrl (kind: string | null): string | null {
			// Platform brand logos vendored locally under public/img/plugin-logos/.
			// Local-hosted SVGs are more robust than a CDN: they ship with the
			// build, work offline in dev, and we never get bit by external slug
			// changes (e.g. simpleicons.org no longer serves Adobe products).
			// Returns null for unknown kinds so the template falls through to
			// mdi-puzzle-outline; the <img> @error handler covers the same
			// fallback path if the file is ever missing from the build.
			const map: Record<string, string> = {
				photoshop: '/img/plugin-logos/photoshop.svg',
				miro: '/img/plugin-logos/miro.svg',
				revit: '/img/plugin-logos/revit.svg',
			};
			return (kind && map[kind]) || null;
		},
		onLogoError (deviceId: string) {
			this.$set(this.logoFailed, deviceId, true);
		},
		pretty (iso?: string): string {
			if (!iso) return 'Never';
			try {
				return new Date(iso).toLocaleString();
			} catch {
				return iso;
			}
		},
	},
});
